import { formatDateTime, formatIsoDate, formatLabel } from '@/utils/format'
import { normalizeBusinessQueryRecord } from '@/utils/businessQueries'

function pickString(raw, ...keys) {
  if (!raw || typeof raw !== 'object') return ''
  for (const key of keys) {
    const value = raw[key]
    if (value != null && String(value).trim()) return String(value).trim()
  }
  return ''
}

function pickList(raw, ...keys) {
  if (!raw || typeof raw !== 'object') return []
  for (const key of keys) {
    if (Array.isArray(raw[key])) {
      return raw[key].map((item) => String(item ?? '').trim()).filter(Boolean)
    }
  }
  return []
}

// Unlike pickString/pickList/pickNumber, a nullable Boolean field's null
// state IS meaningful data (here: "inheriting the org default", not "no
// value yet") -- must be preserved rather than collapsed into a default.
function pickNullableBoolean(raw, ...keys) {
  if (!raw || typeof raw !== 'object') return null
  for (const key of keys) {
    const value = raw[key]
    if (value === true || value === false) return value
  }
  return null
}

function pickNumber(...values) {
  for (const value of values) {
    if (value == null || value === '') continue
    const num = Number(value)
    if (Number.isFinite(num)) return num
  }
  return null
}

/** Display API percents (0–100 or 0–1 fractions). */
export function formatCampaignPercent(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '—'
  const pct = num > 0 && num <= 1 ? num * 100 : num
  return `${Math.round(pct)}%`
}

export function formatCampaignDurationSeconds(value) {
  const sec = Number(value)
  if (!Number.isFinite(sec) || sec < 0) return '—'
  const m = Math.floor(sec / 60)
  const s = Math.round(sec % 60)
  if (m <= 0) return `${s}s`
  return `${m}m ${String(s).padStart(2, '0')}s`
}

export const CAMPAIGN_DISTRIBUTION_MODES = [
  { value: 'OPEN', title: 'Open — any assigned agent works any lead' },
  { value: 'MANUAL', title: 'Manual — admin assigns each lead' },
  { value: 'ROUND_ROBIN', title: 'Round robin — leads split automatically' },
  {
    value: 'EXCEL',
    title: 'Excel — assign leads from a spreadsheet',
  },
  {
    value: 'FIELD_BASED',
    title: 'Field-based — each lead names its agent in a product field',
  },
  {
    value: 'CREATOR_BASED',
    title: 'Creator-based — assigns to whoever created the lead',
  },
]

export const CAMPAIGN_HANDLED_BY_OPTIONS = [
  {
    value: 'AI_AGENT',
    title: 'AI agents',
    subtitle: 'The AI dialer calls the leads',
    icon: 'mdi-robot-outline',
  },
  {
    value: 'HUMAN_AGENT',
    title: 'Human agents',
    subtitle: 'Your team calls via the browser softphone',
    icon: 'mdi-headset',
  },
]

export function campaignHandledByLabel(value) {
  const key = String(value ?? '').trim().toUpperCase()
  if (key === 'AI_AGENT') return 'AI agents'
  if (key === 'HUMAN_AGENT') return 'Human agents'
  return formatLabel(key) || '—'
}

export function campaignDistributionLabel(value) {
  const key = String(value ?? '').trim().toUpperCase()
  if (key === 'OPEN') return 'Open'
  if (key === 'MANUAL') return 'Manual'
  if (key === 'ROUND_ROBIN') return 'Round robin'
  if (key === 'EXCEL') return 'Excel'
  if (key === 'FIELD_BASED') return 'Field-based'
  if (key === 'CREATOR_BASED') return 'Creator-based'
  return formatLabel(key) || '—'
}

export function campaignStatusTone(status) {
  const key = String(status ?? '').trim().toUpperCase()
  if (['COMPLETED', 'DONE', 'FINISHED'].includes(key)) return 'success'
  if (['ACTIVE', 'RUNNING', 'IN_PROGRESS', 'OPEN'].includes(key)) return 'info'
  if (['PROCESSING', 'PAUSED', 'PENDING', 'SCHEDULED'].includes(key)) {
    return 'warning'
  }
  if (['CANCELLED', 'FAILED', 'EXPIRED'].includes(key)) return 'error'
  return 'neutral'
}

/** Human label for campaign (task) lifecycle status. */
export function campaignStatusLabel(status) {
  const key = String(status ?? '').trim().toUpperCase()
  if (key === 'PROCESSING') return 'Processing leads…'
  if (key === 'ACTIVE') return 'Active'
  if (key === 'COMPLETED') return 'Completed'
  if (key === 'EXPIRED') return 'Expired'
  return formatLabel(key) || '—'
}

function parseCampaignDateBoundary(value, endOfDay = false) {
  const raw = String(value ?? '').trim()
  if (!raw) return null
  if (/^\d+$/.test(raw)) {
    const num = Number(raw)
    if (!Number.isFinite(num)) return null
    return num < 1e12 ? num * 1000 : num
  }
  const iso = endOfDay && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T23:59:59.999` : raw
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : null
}

/**
 * Lead status mutations (single + bulk) only work while the campaign is ACTIVE
 * and within start_date / end_date. Backend also enforces this.
 */
export function isCampaignActiveForLeadUpdates(campaign) {
  const status = String(campaign?.status ?? '').trim().toUpperCase()
  if (!status) {
    return { ok: false, reason: 'Campaign status is unknown.' }
  }
  if (status === 'PROCESSING') {
    return {
      ok: false,
      reason: 'This campaign is still processing leads and is not ACTIVE yet.',
    }
  }
  if (status !== 'ACTIVE') {
    return {
      ok: false,
      reason: `This campaign is ${campaignStatusLabel(status)}, not Active.`,
    }
  }

  const now = Date.now()
  const startMs = parseCampaignDateBoundary(
    campaign?.startDate ?? campaign?.start_date,
    false,
  )
  const endMs = parseCampaignDateBoundary(
    campaign?.endDate ?? campaign?.end_date,
    true,
  )
  if (startMs != null && now < startMs) {
    return {
      ok: false,
      reason: 'This campaign has not reached its start date yet.',
    }
  }
  if (endMs != null && now > endMs) {
    return {
      ok: false,
      reason: 'This campaign end date has passed.',
    }
  }
  return { ok: true, reason: '' }
}

function normalizeCampaignMetrics(item) {
  const metrics =
    item?.metrics && typeof item.metrics === 'object' && !Array.isArray(item.metrics)
      ? item.metrics
      : {}

  const totalLeads = pickNumber(
    metrics.total_leads,
    metrics.totalLeads,
    item.total_leads,
    item.totalLeads,
    item.lead_count,
    item.leadCount,
  )
  const completedLeads = pickNumber(
    metrics.completed_leads,
    metrics.completedLeads,
    item.completed_leads,
    item.completedLeads,
    item.completed_count,
  )
  const dialledLeads = pickNumber(
    metrics.dialled_leads,
    metrics.dialledLeads,
    item.dialled_leads,
    item.dialledLeads,
  )
  const completionPercentage = pickNumber(
    metrics.completion_percentage,
    metrics.completionPercentage,
    item.completion_percentage,
    item.completionPercentage,
  )
  const callerPercentage = pickNumber(
    metrics.caller_percentage,
    metrics.callerPercentage,
    item.caller_percentage,
    item.callerPercentage,
  )
  const totalAiCalls = pickNumber(
    metrics.total_ai_calls,
    metrics.totalAiCalls,
    item.total_ai_calls,
    item.totalAiCalls,
  )
  const avgCallDurationSeconds = pickNumber(
    metrics.avg_call_duration_seconds,
    metrics.avgCallDurationSeconds,
    item.avg_call_duration_seconds,
    item.avgCallDurationSeconds,
  )
  const connectedRate = pickNumber(
    metrics.connected_rate,
    metrics.connectedRate,
    item.connected_rate,
    item.connectedRate,
  )

  return {
    totalLeads,
    completedLeads,
    dialledLeads,
    completionPercentage,
    callerPercentage,
    totalAiCalls,
    avgCallDurationSeconds,
    connectedRate,
  }
}

function normalizeAgentBreakdownEntry(item, index = 0) {
  if (!item || typeof item !== 'object') return null
  const agentId = pickString(
    item,
    'agent_id',
    'agentId',
    'user_id',
    'userId',
    'id',
  )
  return {
    id: agentId || `agent-breakdown-${index}`,
    agentId,
    agentName: pickString(item, 'agent_name', 'agentName', 'name', 'email'),
    assignedLeads: pickNumber(
      item.assigned_leads,
      item.assignedLeads,
      item.assigned,
    ),
    progressedLeads: pickNumber(
      item.progressed_leads,
      item.progressedLeads,
      item.progressed,
    ),
    completionPercentage: pickNumber(
      item.completion_percentage,
      item.completionPercentage,
    ),
  }
}

export function normalizeAgentBreakdown(list) {
  if (!Array.isArray(list)) return []
  return list.map(normalizeAgentBreakdownEntry).filter(Boolean)
}

/** Own slice from agent_breakdown for the current viewer. */
export function findOwnAgentBreakdown(breakdown, agentId) {
  const id = String(agentId ?? '').trim()
  if (!id || !Array.isArray(breakdown) || !breakdown.length) return null
  return (
    breakdown.find((entry) => String(entry.agentId ?? '').trim() === id) || null
  )
}

/** Client-side rollup across myCampaigns (API has no pre-aggregated total). */
export function buildMyCampaignsRollup(campaigns = []) {
  const list = Array.isArray(campaigns) ? campaigns : []
  let totalLeads = 0
  let hasLeads = false
  for (const campaign of list) {
    const n = Number(campaign?.metrics?.totalLeads ?? campaign?.totalLeads)
    if (Number.isFinite(n)) {
      totalLeads += n
      hasLeads = true
    }
  }
  return {
    campaignCount: list.length,
    totalLeads: hasLeads ? totalLeads : null,
  }
}

export function normalizeCampaignRecord(item, index = 0) {
  if (!item || typeof item !== 'object') return null

  const campaignId = pickString(item, 'task_id', 'taskId', 'campaign_id', 'campaignId', 'id', 'uuid')
  const status = pickString(item, 'task_status', 'taskStatus', 'status')
  const startDate = pickString(item, 'start_date', 'startDate')
  const endDate = pickString(item, 'end_date', 'endDate')
  const createdOn = pickString(item, 'created_on', 'createdOn', 'created_at')
  const metrics = normalizeCampaignMetrics(item)
  const metricsObj =
    item.metrics && typeof item.metrics === 'object' && !Array.isArray(item.metrics)
      ? item.metrics
      : {}
  const agentBreakdown = normalizeAgentBreakdown(
    item.agent_breakdown ??
      item.agentBreakdown ??
      metricsObj.agent_breakdown ??
      metricsObj.agentBreakdown ??
      [],
  )
  const nextDialAt = pickString(item, 'next_dial_at', 'nextDialAt')
  const lastDialAt = pickString(item, 'last_dial_at', 'lastDialAt')
  const workingHoursStart = pickString(
    item,
    'working_hours_start',
    'workingHoursStart',
  )
  const workingHoursEnd = pickString(
    item,
    'working_hours_end',
    'workingHoursEnd',
  )
  const retryCooldownMinutes = pickNumber(
    item.retry_cooldown_minutes,
    item.retryCooldownMinutes,
  )
  const maxDialAttempts = pickNumber(
    item.max_dial_attempts,
    item.maxDialAttempts,
  )
  // Campaign-level override of the platform's per-lead daily dial ceiling.
  // null = no override, resolves through the product's own default instead.
  const dailyDialCap = pickNumber(item.daily_dial_cap, item.dailyDialCap)
  const dialRatioRaw = pickNumber(item.dial_ratio, item.dialRatio)
  const dialRatio =
    dialRatioRaw != null
      ? Math.min(10, Math.max(1, Math.floor(dialRatioRaw)))
      : 1
  const distributionMode = pickString(
    item,
    'distribution_mode',
    'distributionMode',
  ).toUpperCase()
  const handledBy = pickString(item, 'handled_by', 'handledBy').toUpperCase()
  const agentField = pickString(item, 'agent_field', 'agentField')
  const taskType = pickString(item, 'task_type', 'taskType').toUpperCase() || 'STANDARD'
  // Defaults to OUTGOING so a campaign created before direction existed
  // reads as what it has always been, rather than as an empty value the UI
  // then has to interpret.
  const callDirection = pickString(item, 'call_direction', 'callDirection').toUpperCase() || 'OUTGOING'
  const recurrenceIntervalDays = pickNumber(
    item.recurrence_interval_days,
    item.recurrenceIntervalDays,
  )
  // Was previously never extracted here at all -- the Settings tab's
  // "Capture agreed time" section read campaign.captureDatetimeField, which
  // this normalizer never set, so it silently always hydrated as "Don't
  // save it anywhere" even for a campaign that already had one configured.
  const captureDatetimeField = pickString(item, 'capture_datetime_field', 'captureDatetimeField')
  const captureDatetimeTimeField = pickString(item, 'capture_datetime_time_field', 'captureDatetimeTimeField')
  const captureDatetimePrompt = pickString(item, 'capture_datetime_prompt', 'captureDatetimePrompt')
  // 2026-09-05: general-purpose sibling of the three above -- an array, so
  // no pickString helper (that's string-only); same either-casing
  // tolerance, read straight off whichever key the resolver actually used.
  const captureLeadFields = Array.isArray(item.capture_lead_fields)
    ? item.capture_lead_fields
    : (Array.isArray(item.captureLeadFields) ? item.captureLeadFields : [])
  // Slot booking (2026-09-04): when checks real availability across this
  // campaign's resources and books into captureDatetimeField, instead of
  // captureDatetimeTool accepting whatever time the caller states.
  const slotBookingEnabled =
    item.slot_booking_enabled === true || item.slotBookingEnabled === true
  const slotBookingNotifyWhatsappEnabled =
    item.slot_booking_notify_whatsapp_enabled === true || item.slotBookingNotifyWhatsappEnabled === true
  const slotBookingNotifyEmailEnabled =
    item.slot_booking_notify_email_enabled === true || item.slotBookingNotifyEmailEnabled === true
  const slotBookingConfirmationMessage = pickString(
    item,
    'slot_booking_confirmation_message',
    'slotBookingConfirmationMessage',
  )
  // Where this campaign hands a call over. Tokens are agent:<id> (softphone)
  // or phone:<id> (their mobile) -- resolved server-side into the real
  // hunt-list value, never rendered raw.
  const redirectTargetsRaw = item?.redirect_targets ?? item?.redirectTargets
  const redirectTargets = Array.isArray(redirectTargetsRaw)
    ? redirectTargetsRaw.map((t) => String(t)).filter(Boolean)
    : []
  // What the AI says right before handing the call over. Empty/absent
  // means the platform default line is used.
  const redirectMessage = pickString(item, 'redirect_message', 'redirectMessage')
  // Caller-facing description of who this transfer target is -- what the AI
  // matches a caller's spoken answer against when more than one campaign has
  // a transfer target. Empty/absent falls back to the campaign's own name.
  const redirectDescription = pickString(item, 'redirect_description', 'redirectDescription')
  const anchorField = pickString(item, 'anchor_field', 'anchorField')
  // Fixed calendar date (ONE_TIME only) -- takes priority over anchorField
  // when set. "YYYY-MM-DD", matching startDate/endDate's own convention.
  const anchorDate = pickString(item, 'anchor_date', 'anchorDate')
  const intervalField = pickString(item, 'interval_field', 'intervalField')
  const scheduleType = pickString(item, 'schedule_type', 'scheduleType').toUpperCase()
  const scheduleTypeField = pickString(item, 'schedule_type_field', 'scheduleTypeField')
  const scheduleFrequency = pickNumber(item.schedule_frequency, item.scheduleFrequency)
  const scheduleFrequencyField = pickString(item, 'schedule_frequency_field', 'scheduleFrequencyField')
  const scheduleTime = pickString(item, 'schedule_time', 'scheduleTime')
  const scheduleTimeField = pickString(item, 'schedule_time_field', 'scheduleTimeField')
  const scheduleDayOfWeek = pickString(item, 'schedule_day_of_week', 'scheduleDayOfWeek').toUpperCase()
  const scheduleDayOfWeekField = pickString(item, 'schedule_day_of_week_field', 'scheduleDayOfWeekField')
  const scheduleDayOfMonthRaw = item.schedule_day_of_month ?? item.scheduleDayOfMonth
  const scheduleDayOfMonth = scheduleDayOfMonthRaw == null || scheduleDayOfMonthRaw === '' ? '' : String(scheduleDayOfMonthRaw)
  const scheduleDayOfMonthField = pickString(item, 'schedule_day_of_month_field', 'scheduleDayOfMonthField')
  // Only present on AI_AGENT campaigns (listTasks joins these in from the
  // campaign's own auto-created action) -- the current greeting/mission
  // prompt actually in effect, for review/override in the edit dialog.
  const greeting = pickString(item, 'greeting')
  const missionPrompt = pickString(item, 'mission_prompt', 'missionPrompt')
  // null = this campaign has never set its own override, so it's inheriting
  // the org's own disclaimer_enabled setting -- true/false is an explicit
  // per-campaign override regardless of that org default.
  const disclaimerEnabled = pickNullableBoolean(item, 'disclaimer_enabled', 'disclaimerEnabled')
  // Voice mode -- absent/blank reads as AI_SPEAK everywhere, same
  // additivity guarantee every other new campaign field here follows.
  const voiceMode = pickString(item, 'voice_mode', 'voiceMode').toUpperCase() || 'AI_SPEAK'
  const voiceSource = pickString(item, 'voice_source', 'voiceSource').toUpperCase()
  const voicePersonalization = pickString(item, 'voice_personalization', 'voicePersonalization').toUpperCase()
  const voiceScriptText = pickString(item, 'voice_script_text', 'voiceScriptText')
  const voiceAudioRawKey = pickString(item, 'voice_audio_raw_key', 'voiceAudioRawKey')
  const voiceAudioFilename = pickString(item, 'voice_audio_filename', 'voiceAudioFilename')
  const voiceLanguage = pickString(item, 'voice_language', 'voiceLanguage')
  // Server-computed only -- never sent back on save, just shown/used to
  // tell whether a SAME_FOR_ALL combination already has resolved audio.
  const voiceAudioResolvedKey = pickString(item, 'voice_audio_resolved_key', 'voiceAudioResolvedKey')
  const voiceAudioResolvedVersion = pickString(item, 'voice_audio_resolved_version', 'voiceAudioResolvedVersion')
  // AI-to-AI transfer (KAN-44): always a real true/false server-side
  // (editTask defaults it to false, never leaves it null), unlike
  // disclaimerEnabled above -- no inherited/unset third state to preserve.
  const aiTransferEligible = Boolean(item.ai_transfer_eligible)
  const filtersRaw = item.filters
  const filters = Array.isArray(filtersRaw)
    ? filtersRaw
        .map((row) => ({
          field: pickString(row, 'field'),
          operator: pickString(row, 'operator').toUpperCase() || 'EQUALS',
          values: Array.isArray(row?.values)
            ? row.values.map((value) =>
                typeof value === 'number' ? value : String(value ?? '').trim(),
              )
            : [],
        }))
        .filter((row) => row.field && row.operator)
    : []

  return {
    id: campaignId || `campaign-${index}`,
    campaignId,
    name: pickString(item, 'task_name', 'taskName', 'name') || 'Untitled campaign',
    description: pickString(item, 'description'),
    status: status.toUpperCase(),
    statusLabel: campaignStatusLabel(status),
    statusTone: campaignStatusTone(status),
    handledBy,
    distributionMode,
    agentField,
    taskType,
    callDirection,
    captureDatetimeField,
    captureDatetimeTimeField,
    captureDatetimePrompt,
    captureLeadFields,
    slotBookingEnabled,
    slotBookingNotifyWhatsappEnabled,
    slotBookingNotifyEmailEnabled,
    slotBookingConfirmationMessage,
    isSystem: item?.is_system === true || item?.isSystem === true,
    recurrenceIntervalDays,
    redirectTargets,
    redirectMessage,
    redirectDescription,
    anchorField,
    anchorDate,
    intervalField,
    scheduleType,
    scheduleTypeField,
    scheduleFrequency,
    scheduleFrequencyField,
    scheduleTime,
    scheduleTimeField,
    scheduleDayOfWeek,
    scheduleDayOfWeekField,
    scheduleDayOfMonth,
    scheduleDayOfMonthField,
    greeting,
    missionPrompt,
    disclaimerEnabled,
    voiceMode,
    voiceSource,
    voicePersonalization,
    voiceScriptText,
    voiceAudioRawKey,
    voiceAudioFilename,
    voiceLanguage,
    voiceAudioResolvedKey,
    voiceAudioResolvedVersion,
    aiTransferEligible,
    filters,
    contextNames: pickList(item, 'context_names', 'contextNames'),
    assignedAgentIds: pickList(item, 'assigned_agent_ids', 'assignedAgentIds'),
    autoDialEnabled:
      item.auto_dial_enabled === true || item.autoDialEnabled === true,
    restrictToKnownCallers:
      item.restrict_to_known_callers === true || item.restrictToKnownCallers === true,
    // Defaults true for campaigns predating this flag -- only an explicit
    // false means this campaign opts out of receiving leads auto-adopted
    // from elsewhere in the same product.
    autoAdoptMatchingLeads:
      item.auto_adopt_matching_leads === false || item.autoAdoptMatchingLeads === false
        ? false
        : true,
    // One-time creation choice -- true means it started empty and only
    // fills from leads created/re-submitted after the campaign existed.
    onlyFutureLeads:
      item.only_future_leads === true || item.onlyFutureLeads === true,
    // Dashboard Digest -- periodic KPI + agent-activity email/WhatsApp, off
    // by default (opt-in per campaign).
    sendDashboardDigestByEmail:
      item.send_dashboard_digest_by_email === true || item.sendDashboardDigestByEmail === true,
    dashboardDigestEmailAddresses: pickList(item, 'dashboard_digest_email_addresses', 'dashboardDigestEmailAddresses'),
    sendDashboardDigestByWhatsapp:
      item.send_dashboard_digest_by_whatsapp === true || item.sendDashboardDigestByWhatsapp === true,
    dashboardDigestWhatsappNumbers: pickList(item, 'dashboard_digest_whatsapp_numbers', 'dashboardDigestWhatsappNumbers'),
    dashboardDigestCcAgents:
      item.dashboard_digest_cc_agents === true || item.dashboardDigestCcAgents === true,
    dashboardDigestTimes: pickList(item, 'dashboard_digest_times', 'dashboardDigestTimes'),
    // Independent weekly/monthly cadences alongside the daily times above.
    dashboardDigestWeeklyEnabled:
      item.dashboard_digest_weekly_enabled === true || item.dashboardDigestWeeklyEnabled === true,
    dashboardDigestWeeklyDay: pickString(item, 'dashboard_digest_weekly_day', 'dashboardDigestWeeklyDay'),
    dashboardDigestWeeklyTime: pickString(item, 'dashboard_digest_weekly_time', 'dashboardDigestWeeklyTime'),
    dashboardDigestMonthlyEnabled:
      item.dashboard_digest_monthly_enabled === true || item.dashboardDigestMonthlyEnabled === true,
    dashboardDigestMonthlyDay: pickNumber(item.dashboard_digest_monthly_day, item.dashboardDigestMonthlyDay),
    dashboardDigestMonthlyTime: pickString(item, 'dashboard_digest_monthly_time', 'dashboardDigestMonthlyTime'),
    // KAN-32 Report Digest -- unlike the flat dashboard_digest_* scalars
    // above, this is a nested object keyed by report type (callLog/
    // statusHistory/agentActivity/leadDetail), so it's passed through as one
    // object rather than exploded into dozens of flat camelCase fields.
    // Same bug class already hit twice this session (ORG_META_FIELDS in
    // session.js, report_column_config in contexts.js) -- this function is a
    // fully-enumerated allowlist, not a passthrough, so a field absent here
    // silently reads as "never configured" forever regardless of what's
    // actually saved.
    reportDigestConfig: (item.report_digest_config && typeof item.report_digest_config === 'object')
      ? item.report_digest_config
      : (item.reportDigestConfig && typeof item.reportDigestConfig === 'object' ? item.reportDigestConfig : {}),
    reportDigestLastSent: (item.report_digest_last_sent && typeof item.report_digest_last_sent === 'object')
      ? item.report_digest_last_sent
      : (item.reportDigestLastSent && typeof item.reportDigestLastSent === 'object' ? item.reportDigestLastSent : {}),
    workingHoursStart,
    workingHoursEnd,
    retryCooldownMinutes,
    maxDialAttempts,
    dailyDialCap,
    dialRatio,
    nextDialAt,
    nextDialAtLabel: nextDialAt ? formatDateTime(nextDialAt) : '—',
    lastDialAt,
    lastDialAtLabel: lastDialAt ? formatDateTime(lastDialAt) : '—',
    startDate,
    endDate,
    startDateLabel: startDate ? formatIsoDate(startDate) : '—',
    endDateLabel: endDate ? formatIsoDate(endDate) : '—',
    createdOn,
    createdOnLabel: createdOn ? formatDateTime(createdOn) : '—',
    createdBy: pickString(item, 'created_by', 'createdBy'),
    metrics,
    agentBreakdown,
    totalLeads: metrics.totalLeads,
    completedLeads: metrics.completedLeads,
    dialledLeads: metrics.dialledLeads,
    isAiHandled: handledBy === 'AI_AGENT',
    showsAgentBreakdown: agentBreakdown.length > 0,
    raw: item,
  }
}

export function normalizeCampaignsList(rawList) {
  const list = Array.isArray(rawList) ? rawList : []
  return list
    .map((item, index) => normalizeCampaignRecord(item, index))
    .filter(Boolean)
}

/** Campaign leads share the lead shape, plus per-campaign agent assignment. */
export function normalizeCampaignLeadRecord(item, index = 0) {
  const lead = normalizeBusinessQueryRecord(item, index)
  if (!lead) return null
  const statusUpdatedOn = pickString(
    item,
    'status_updated_on',
    'statusUpdatedOn',
    'membership_status_updated_on',
  )
  // QUEUED / IN_PROGRESS / DONE -- computed server-side by getTaskLeads'
  // own callProgress() from data already on the row (dial_attempt_count,
  // request_status, call_history). Only meaningful for AI_AGENT campaigns;
  // callers gate display on that, this just carries the raw value through.
  const callProgress = pickString(item, 'call_progress', 'callProgress').toUpperCase()
  return {
    ...lead,
    assignedAgentId: pickString(
      item,
      'task_assigned_to',
      'taskAssignedTo',
      'assigned_agent_id',
      'assignedAgentId',
      'agent_id',
      'agentId',
    ),
    assignedAgentName: pickString(
      item,
      'assigned_agent_name',
      'assignedAgentName',
      'agent_name',
      'agentName',
    ),
    statusUpdatedOn,
    statusUpdatedOnLabel: statusUpdatedOn ? formatDateTime(statusUpdatedOn) : '',
    progressed: Boolean(statusUpdatedOn),
    callProgress: callProgress || 'QUEUED',
  }
}

export function normalizeCampaignLeadsList(rawList) {
  const list = Array.isArray(rawList) ? rawList : []
  return list
    .map((item, index) => normalizeCampaignLeadRecord(item, index))
    .filter(Boolean)
}
